import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { handleGetRequest, handlePostRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { ArrowLeftIcon, EyeIcon } from '@heroicons/react/24/outline'
import { ChatButton } from '../../components/common/ChatButton'
import { CallButton } from '../../components/common/CallButton'
import { RequestBidModal } from '../../components/bids/RequestBidModal'
import { useAuth } from '../../contexts/AuthContext'
import AuthPromptModal from '../../components/common/AuthPromptModal'
import ProductImageCarousel from '../../components/common/ProductImageCarousel'
import { CHAT_CONTEXT_TYPES, CONTENT_TYPES } from '../../constants/contentTypes'
import ReportButton from '../../components/common/ReportButton'
import USER_TYPE from '../../constants/userType'
import { EVENT_TYPES } from '../../constants/eventTypes'
import { SUBJECT_TYPE } from '../../constants/subjectType'
import useProductCategories from '../../hooks/useProductCategories'
import SeoHelmet from '../../components/common/SeoHelmet'
import DisplayShopName from '../../components/common/DisplayShopName'
import BackButtonAndTitle from '../../components/retailer-shop/BackButtonAndTitle'

const ProductBidRequestPage = () => {
  const { getCategoryById } = useProductCategories()
  const { userType } = useAuth()
  const { productSlug, shopSlug } = useParams()
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const submitBidMutation = useMutation({
    mutationFn: (bidData) => handlePostRequest('/customers/bids/request', bidData),
    onSuccess: (data) => {
      const newBidId = data?.data?.id
      if (newBidId) {
        QueryKeys.bidsList.invalidateAll()
        navigate('/bids/success', { state: { bidId: newBidId } })
      } else {
        handleCloseModal()
      }
    },
  })

  const { data: shopProductResponse } = useQuery({
    queryKey: QueryKeys.shopProductDetails(productSlug, userType),
    queryFn: () => handleGetRequest(`/customers/${shopSlug}/products/${productSlug}`),
    enabled: !!productSlug && !!shopSlug,
  })

  const product = shopProductResponse?.data

  const logProductViewMutation = useMutation({
    mutationFn: async (payload) => {
      return handlePostRequest('/analytics/log-activity', payload)
    },
    onSuccess: () => {
      QueryKeys.shopProductDetails.invalidate(productSlug)
    },
  })

  useEffect(() => {
    if (!product?.shopProductId) return
    if (userType !== USER_TYPE.customer) return

    const logProductView = async () => {
      await logProductViewMutation.mutateAsync({
        eventType: EVENT_TYPES.PRODUCT_VIEW.key,
        subjectType: SUBJECT_TYPE.PRODUCT,
        subjectId: product.shopProductId,
      })
    }

    logProductView()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.shopProductId])

  const handleRequestBidClick = () => setIsModalOpen(true)
  const handleCloseModal = () => setIsModalOpen(false)
  const handleBidSubmit = (prodId, quantity) => {
    submitBidMutation.mutate({ productId: prodId, requestQuantity: quantity })
  }

  const category = getCategoryById(product?.categoryId)

  return (
    <div className="main-container">
      <SeoHelmet
        title={product?.name}
        titleSuffix="Buy Online from Eastleigh"
        description={
          product?.description.length > 150
            ? product?.description.slice(0, 147) + '...'
            : product?.description
        }
        image={product?.productImages?.[0]?.imageUrl || ''}
        url={`/retailers/${shopSlug}/products/${productSlug}`}
      />

      {/* Header with back button and report - full width */}
      <div className="bg-background rounded-xl shadow-sm mb-4 lg:mb-6">
        <div className="flex items-center justify-between px-4 lg:px-8 py-4 lg:py-6">
          <BackButtonAndTitle
            title={`View ${product?.name || 'Product Details'}`}
            className="mb-0"
          />
          <div className="flex items-center">
            <AuthPromptModal>
              <ReportButton
                reportTarget={product?.name}
                contentId={product?.productId}
                contentType={CONTENT_TYPES.product}
              />
            </AuthPromptModal>
          </div>
        </div>
      </div>

      {/* Main product content */}
      <div className="bg-background rounded-xl shadow-sm overflow-hidden">
        <div className="lg:grid lg:grid-cols-2 lg:gap-12 xl:gap-16">
          {/* Left side - Product Images */}
          <div className="lg:p-8 xl:p-12">
            <div className="lg:sticky lg:top-8">
              <ProductImageCarousel productImages={product?.productImages} />
            </div>
          </div>

          {/* Right side - Product Details */}
          <div className="p-6 lg:p-8 xl:p-12 lg:border-l lg:border-gray-100">
            <div className="space-y-6 lg:space-y-8">
              {/* Product Title and Price */}
              <div>
                <h1 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 leading-tight mb-4">
                  {product?.name}
                </h1>

                {product?.priceText && (
                  <div className="text-3xl lg:text-4xl xl:text-5xl font-bold text-orange-600 mb-4">
                    {product?.priceText}
                  </div>
                )}

                <div className="flex items-center text-gray-600 mb-6">
                  <EyeIcon className="h-5 w-5 mr-2" />
                  <span className="text-sm lg:text-base">{product?.totalProductViews} views</span>
                </div>
              </div>

              {/* Category and Shop Info */}
              <div className="space-y-4">
                {category && (
                  <div>
                    <span className="text-sm text-gray-500 block mb-2">Category</span>
                    <Link to={`/categories/${category.categorySlug}`}>
                      <span className="inline-flex items-center px-3 py-2 rounded-lg bg-neutral-200-100 text-gray-700 text-sm font-medium hover:bg-neutral-200 transition-colors cursor-pointer">
                        {category.name}
                      </span>
                    </Link>
                  </div>
                )}

                {product?.shopName && (
                  <div>
                    <span className="text-sm text-gray-500 block mb-2">Seller</span>
                    <Link to={`/${product?.shopType}s/${product?.shopSlug}/products`}>
                      <div className="text-blue-600 text-lg font-medium hover:text-blue-700 transition-colors">
                        <DisplayShopName
                          shop={{
                            name: product?.shopName,
                            isPremium: product.isShopPremium,
                            isVerified: product.isShopVerified,
                          }}
                        />
                      </div>
                    </Link>
                  </div>
                )}
              </div>

              {/* Product Description */}
              <div>
                <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mb-3">Description</h3>
                <p className="text-gray-700 leading-relaxed lg:text-lg">{product?.description}</p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4 pt-4 lg:pt-8">
                <AuthPromptModal>
                  <button
                    onClick={handleRequestBidClick}
                    disabled={submitBidMutation.isLoading}
                    className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-4 lg:py-5 px-8 rounded-xl text-lg lg:text-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {submitBidMutation.isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Submitting Request...
                      </span>
                    ) : (
                      'Request Bids'
                    )}
                  </button>
                </AuthPromptModal>

                <div className="grid grid-cols-2 gap-4">
                  <AuthPromptModal>
                    <ChatButton
                      className="w-full bg-background border-2 border-gray-300 text-gray-700 font-semibold py-3 lg:py-4 px-6 rounded-xl hover:border-gray-400 hover:bg-neutral-200-50 transition-all duration-200"
                      chatConfig={{
                        targetId: product?.shopId,
                        contextType: CHAT_CONTEXT_TYPES.product,
                        contextId: product?.shopProductId,
                        targetUserType: USER_TYPE.retailer,
                        description: (
                          <>
                            Contact the retailer for more details about{' '}
                            <strong>{product?.name}</strong>
                          </>
                        ),
                      }}
                    >
                      <span className="text-sm lg:text-base">Chat with Retailer</span>
                    </ChatButton>
                  </AuthPromptModal>

                  <AuthPromptModal>
                    <CallButton
                      className="w-full bg-background border-2 border-gray-300 text-gray-700 font-semibold py-3 lg:py-4 px-6 rounded-xl hover:border-gray-400 hover:bg-neutral-200-50 transition-all duration-200"
                      label={<span className="text-sm lg:text-base">Call Retailer</span>}
                      targetPhoneNumber={product?.shopPhoneNumber}
                    />
                  </AuthPromptModal>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RequestBidModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleBidSubmit}
        productName={product?.name}
        productId={product?.productId}
      />
    </div>
  )
}

export default ProductBidRequestPage
